import {useImportModalStore} from '../Accounts/useImportModalStore';
import {FileValidationError} from '../../lib/validateFile';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import {Information} from '@carbon/icons-react';
import {FileChooser} from '../File/FileChooser';
import * as Sentry from '@sentry/react';
import {isRecommendedAccountsImportRow} from '../../shared/import';
import {FileValidator} from '../File/FileValidator';
import {FileValidationErrors} from '../File/FileValidationErrors';
import {useForm} from 'react-hook-form';
import {TagName} from '../../shared/validation';
import {useUploadFile} from './useUploadFileMutation';
import {useQueueCustomerAccountsImport} from './useQueueCustomerAccountsImport';
import {useSaveTagMutation, useTags} from '../../hooks/api/tags';

const FileChooserStep = () => {
  const {selectedFile, setState} = useImportModalStore();

  return (
    <Flex direction="column" gap="4" w="100%">
      {!selectedFile && (
        <InfoBox>
          Your CSV must include columns for Domain, Linkedin URL, or both.{' '}
          <ImportTemplateLink text="Download a template" />
        </InfoBox>
      )}
      <FileChooser
        selectedFile={selectedFile}
        onSelectedFile={(file) => {
          setState({
            selectedFile: file,
            step: 'validateFile',
          });
        }}
      />
    </Flex>
  );
};

const ValidateFileStep = () => {
  const {selectedFile, setState} = useImportModalStore();
  if (!selectedFile) {
    Sentry.captureMessage('validate file step but no file selected');
    return <></>;
  }

  return (
    <FileValidator
      maxRows={10_000}
      onFileValidate={(errors) => {
        if (errors.size > 0) {
          setState({
            fileValidationErrors: errors,
            step: 'errors',
          });
        } else {
          setState({
            fileValidationErrors: null,
            step: 'uploadFile',
          });
        }
      }}
      rowSchemaTypeguard={isRecommendedAccountsImportRow}
      selectedFile={selectedFile}
    />
  );
};

const UploadFileStep = ({
  isPending,
  uploadFile,
  formId,
}: {
  isPending: boolean;
  uploadFile: (tagName: string) => void;
  formId: string;
}) => {
  const {
    formState: {errors},
    handleSubmit,
    getValues,
    watch,
    register,
  } = useForm<{tagName: string}>();
  const tagName = watch('tagName') || '';
  const characterCount = tagName.trim().length;

  const handleImportClick = () => {
    uploadFile(getValues('tagName'));
  };

  return (
    <Flex
      direction="column"
      alignItems="flex-start"
      gap="8"
      as="form"
      onSubmit={handleSubmit(handleImportClick)}
      width="100%"
      id={formId}
    >
      <InfoBox>
        Your CSV looks great! Now, create a new tag to apply to these accounts.
      </InfoBox>
      <Flex direction="column" gap="8" w="100%">
        <FormControl isRequired isInvalid={!!errors.tagName} width="100%">
          <Box
            fontWeight="medium"
            fontSize="xs"
            mb="2"
            textTransform="uppercase"
          >
            Tag Name
          </Box>
          <Input
            isDisabled={isPending}
            isInvalid={!!errors.tagName}
            size="md"
            placeholder="eg. Mid-Market Customers"
            maxLength={TagName.maxLength}
            {...register('tagName', {
              minLength: TagName.minLength,
              maxLength: TagName.maxLength,
              pattern: TagName.pattern,
              required: true,
              setValueAs: (value) => value.trim(),
            })}
          />

          {errors.tagName ? (
            <FormErrorMessage>
              Tag names should be between {TagName.minLength} and{' '}
              {TagName.maxLength} characters, and contain only letters and
              numbers.
            </FormErrorMessage>
          ) : (
            <Box fontSize="xs" color="kgray.300" mt="1">
              {characterCount}/{TagName.maxLength} characters
            </Box>
          )}
        </FormControl>
      </Flex>
    </Flex>
  );
};

const validationErrorText = (fileValidationError: FileValidationError) => {
  switch (fileValidationError) {
    case 'file_too_large':
      return 'File is too large. Max of 10K rows per import.';
    case 'missing_columns':
      return (
        <Box>
          Required columns not found. Start with a{' '}
          <ImportTemplateLink text="CSV import template" />.
        </Box>
      );
  }
};

const ValidationErrorsStep = () => {
  const {fileValidationErrors} = useImportModalStore();
  if (!fileValidationErrors) {
    Sentry.captureMessage('validation errors step but no errors detected');
    return <></>;
  }

  return (
    <FileValidationErrors
      fileValidationErrors={fileValidationErrors}
      validationErrorFn={validationErrorText}
    />
  );
};

const Footer = ({children}: {children: React.ReactNode}) => {
  return (
    <ModalFooter
      borderTop="1px"
      borderColor="kgray.200"
      justifyContent="flex-start"
      p="6"
      gap="2"
    >
      {children}
    </ModalFooter>
  );
};

const useCustomerAccountsImportFlow = (
  options: {
    onSuccess?: (taskId: string) => void;
    onError?: () => void;
  } = {}
) => {
  const toast = useToast();
  const {data: tags, isPending: isTagsPending} = useTags({type: 'static'});

  const uploadFileMutation = useUploadFile();
  const saveTagMutation = useSaveTagMutation();
  const queueCustomerAccountsImport = useQueueCustomerAccountsImport();
  const isPending =
    uploadFileMutation.isPending ||
    queueCustomerAccountsImport.isPending ||
    saveTagMutation.isPending ||
    isTagsPending;

  const executeImport = async (file: File, tagName: string) => {
    try {
      // Check if tag already exists
      const existingTag = tags?.find(
        (tag) => tag.name.toLowerCase() === tagName.toLowerCase()
      );
      // Create the tag if it doesn't exist
      const tag =
        existingTag ??
        (await saveTagMutation.mutateAsync({
          name: tagName,
          type: 'static' as const,
        }));

      // Use the file name as the source
      const source = file.name;

      // Upload the file to Cloud Storage
      uploadFileMutation.mutate(
        {file},
        {
          onSuccess: async (signedUrlResponse) => {
            // Queue the import task
            const {taskId} = await queueCustomerAccountsImport.mutateAsync({
              cloudStorageBucket: signedUrlResponse.cloudStorageBucket,
              cloudStorageFilePath: signedUrlResponse.cloudStorageFilePath,
              source,
              tagId: tag._id,
            });
            toast({
              status: 'success',
              title: 'Import process started!',
              duration: 2_000,
              isClosable: true,
            });
            options.onSuccess?.(taskId);
          },
          onError: () => {
            toast({
              status: 'error',
              title: 'Error uploading file',
              duration: 2_000,
              isClosable: true,
            });
            options.onError?.();
          },
        }
      );
    } catch (error) {
      Sentry.captureException(error, {extra: {tagName}});
      toast({
        status: 'error',
        title: 'Error creating tag',
        duration: 2_000,
        isClosable: true,
      });
      options.onError?.();
    }
  };

  return {
    executeImport,
    isPending,
  };
};

const uploadFileFormId = 'upload-file-form';
export const CustomerAccountsImportModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: (taskId: string | null) => void;
}) => {
  const {step, reset, setState, selectedFile} = useImportModalStore();

  const closeModal = (taskId: string | null) => {
    reset();
    onClose(taskId);
  };

  const {executeImport, isPending} = useCustomerAccountsImportFlow({
    onSuccess: (taskId) => closeModal(taskId),
    onError: () => closeModal(null),
  });

  const handleImport = async (tagName: string) => {
    if (!selectedFile || !tagName) {
      return;
    }
    await executeImport(selectedFile, tagName);
  };

  return (
    <Modal isCentered isOpen={isOpen} onClose={() => closeModal(null)}>
      <ModalOverlay />
      <ModalContent minW="800" rounded="2xl">
        <ModalHeader fontWeight="normal" pb="1" pt="6">
          Import an Account List
        </ModalHeader>
        <ModalHeader fontWeight="normal" fontSize="sm" py="0" color="kgray.300">
          Upload a CSV to apply a new tag to all of the accounts in the list.
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          p="6"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          {step === 'chooseFile' && <FileChooserStep />}
          {step === 'validateFile' && <ValidateFileStep />}
          {step === 'uploadFile' && (
            <UploadFileStep
              isPending={isPending}
              uploadFile={(tagName: string) => handleImport(tagName)}
              formId={uploadFileFormId}
            />
          )}
          {step === 'errors' && <ValidationErrorsStep />}
        </ModalBody>
        {step === 'uploadFile' && (
          <Footer>
            <Button
              colorScheme="kbuttonblue"
              isDisabled={isPending || !selectedFile}
              isLoading={isPending}
              form={uploadFileFormId}
              type="submit"
            >
              Import
            </Button>
            <Button variant="ghost" onClick={() => closeModal(null)}>
              Cancel
            </Button>
          </Footer>
        )}
        {step === 'errors' && (
          <Footer>
            <Button
              colorScheme="kbuttonblue"
              onClick={() =>
                setState({
                  selectedFile: null,
                  step: 'chooseFile',
                })
              }
            >
              Choose Another File
            </Button>
          </Footer>
        )}
      </ModalContent>
    </Modal>
  );
};

const InfoBox = ({children}: {children: React.ReactNode}) => {
  return (
    <Box
      backgroundColor="kgray.100"
      p="4"
      gap="4"
      rounded="lg"
      border="1px"
      borderColor="kgray.200"
      width="100%"
    >
      <Box alignItems="center" display="flex" gap="2">
        <Information />
        <Text>{children}</Text>
      </Box>
    </Box>
  );
};

const ImportTemplateLink = ({text}: {text: string}) => {
  return (
    <Link
      color="kblue.400"
      textDecoration="underline"
      download={true}
      href="/Keyplay_CustomerAccounts_Import_Template.csv"
      display="inline"
    >
      {text}
    </Link>
  );
};
